import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchVapidPublicKey, subscribePush, unsubscribePush, fetchPushSubscriptions } from '@/api/client';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { toast } from 'sonner';

type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

interface PushSubscriptionRecord {
  id: string;
  endpoint: string;
  user_agent: string | null;
  created_at: string;
  last_success_at: string | null;
  is_active: boolean;
}

interface UsePushNotificationsReturn {
  permission: PushPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  subscriptions: PushSubscriptionRecord[];
  requestPermission: () => Promise<void>;
  unsubscribeCurrentDevice: () => Promise<void>;
  unsubscribe: (endpoint: string) => Promise<void>;
  sendTestNotification: () => void;
}

// Helper: converter chave VAPID de base64url para Uint8Array
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer as ArrayBuffer;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const queryClient = useQueryClient();
  const [permission, setPermission] = useState<PushPermission>(() => {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission as PushPermission;
  });

  // Buscar chave VAPID
  const { data: vapidData } = useQuery({
    queryKey: [...queryKeys.push.vapidKey],
    queryFn: fetchVapidPublicKey,
    staleTime: Infinity,
    enabled: permission !== 'unsupported',
  });

  // Buscar inscrições do usuário
  const { data: subscriptions = [], isLoading } = useQuery<PushSubscriptionRecord[]>({
    queryKey: [...queryKeys.push.subscriptions],
    queryFn: fetchPushSubscriptions,
    staleTime: 60 * 1000,
    enabled: permission === 'granted',
  });

  // Verificar se o navegador atual está inscrito
  const [isSubscribed, setIsSubscribed] = useState(false);
  useEffect(() => {
    if (permission !== 'granted') return;
    navigator.serviceWorker?.ready.then((registration) => {
      registration.pushManager.getSubscription().then((sub) => {
        setIsSubscribed(!!sub);
      });
    });
  }, [permission]);

  // Mutation para cancelar inscrição
  const unsubscribeMutation = useMutation({
    mutationFn: unsubscribePush,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.push.subscriptions] });
      toast.success('Dispositivo removido');
    },
  });

  // Desinscrever o dispositivo atual
  const unsubscribeCurrentDevice = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker?.ready;
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await unsubscribePush(subscription.endpoint);
        await subscription.unsubscribe();
        setIsSubscribed(false);
        queryClient.invalidateQueries({ queryKey: [...queryKeys.push.subscriptions] });
        toast.success('Notificações desativadas neste dispositivo');
      }
    } catch {
      toast.error('Erro ao desativar notificações');
    }
  }, [queryClient]);

  // Solicitar permissão e inscrever
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      toast.error('Notificações push não são suportadas neste navegador');
      return;
    }

    const result = await Notification.requestPermission();
    setPermission(result as PushPermission);

    if (result !== 'granted') {
      toast.warning('Permissão de notificação negada');
      return;
    }

    if (!vapidData?.public_key) {
      toast.error('Chave VAPID não disponível');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.public_key),
      });

      const json = subscription.toJSON();
      await subscribePush({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: json.keys?.p256dh || '',
          auth: json.keys?.auth || '',
        },
        user_agent: navigator.userAgent,
      });

      setIsSubscribed(true);
      queryClient.invalidateQueries({ queryKey: [...queryKeys.push.subscriptions] });
      toast.success('Notificações ativadas com sucesso!');
    } catch {
      toast.error('Erro ao ativar notificações');
    }
  }, [vapidData, queryClient]);

  return {
    permission,
    isSubscribed,
    isLoading,
    subscriptions,
    requestPermission,
    unsubscribeCurrentDevice,
    unsubscribe: (endpoint: string) => unsubscribeMutation.mutateAsync(endpoint),
    sendTestNotification: () => {
      toast.info('Notificação de teste enviada');
    },
  };
}
